import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Snackbar,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Breadcrumbs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import FolderIcon from "@mui/icons-material/Folder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUpload from "./FileUpload";
import FilePreview from "./FilePreview";
import axios from "axios";
import { refreshFiles } from "../reducers";
import { decoratedFetch, decoratedAxiosPost } from "../req_utils";
import {
  GENESIS_LOGO_COLOR,
  SUPPORTED_PREVIEW_FILE_TYPES,
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
} from "../constants";
import ThemedTypography from "./ThemedTypography";
import {
  parseListProjectFilesResponse,
  enrichFilesWithFolderPath,
  getChildFolderPaths,
  mergeFolderPaths,
  buildFolderRow,
  isFolderRowId,
  getFolderLabel,
} from "../file_folder_utils";
import ThemedDataGrid from "./ThemedDataGrid";
import ThemedIconButton from "./ThemedIconButton";

function FileTab({ value, index }) {
  const refreshFileList = useSelector((state) => state.refreshFiles);
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const dispatch = useDispatch();
  const [projectFiles, setProjectFiles] = useState([]);
  const [folderPaths, setFolderPaths] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [gridKey, setGridKey] = useState(false);
  const [removeFilesLoading, setRemoveFilesLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState("");
  const [previewFile, setPreviewFile] = useState("");
  const [previewFileLink, setPreviewFileLink] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { project_id } = useParams();
  const [rowIdToItem, setRowIdToItem] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [folderBreadcrumbs, setFolderBreadcrumbs] = useState([]);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [createFolderLoading, setCreateFolderLoading] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetFolderPath, setMoveTargetFolderPath] = useState("");
  const [moveFilesLoading, setMoveFilesLoading] = useState(false);

  const allFolderPaths = useMemo(
    () => mergeFolderPaths(folderPaths, projectFiles),
    [folderPaths, projectFiles],
  );

  const childFolderPaths = useMemo(
    () => getChildFolderPaths(allFolderPaths, currentFolderPath),
    [allFolderPaths, currentFolderPath],
  );

  const folderRows = useMemo(
    () => childFolderPaths.map(buildFolderRow),
    [childFolderPaths],
  );

  const visibleFiles = useMemo(() => {
    return projectFiles.filter(
      (file) => (file.folder_path || "") === currentFolderPath,
    );
  }, [projectFiles, currentFolderPath]);

  const visibleRows = useMemo(
    () => [...folderRows, ...visibleFiles],
    [folderRows, visibleFiles],
  );

  const previewableFiles = useMemo(() => {
    return visibleFiles.filter((file) => !file.is_folder);
  }, [visibleFiles]);

  const folderMoveOptions = useMemo(() => {
    return allFolderPaths
      .map((path) => ({
        path,
        label: getFolderLabel(path),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allFolderPaths]);

  const invalidMoveTargets = useMemo(() => {
    const invalid = new Set();
    selectedFiles.forEach((rowId) => {
      if (!isFolderRowId(rowId)) {
        return;
      }
      const folderPath = String(rowId).slice("folder:".length);
      invalid.add(folderPath);
      allFolderPaths.forEach((path) => {
        if (path === folderPath || path.startsWith(`${folderPath}/`)) {
          invalid.add(path);
        }
      });
    });
    return invalid;
  }, [selectedFiles, allFolderPaths]);

  const selectableMoveTargets = folderMoveOptions.filter(
    (folder) => !invalidMoveTargets.has(folder.path),
  );

  useEffect(() => {
    if (refreshFileList) {
      listProjectFiles();
      dispatch(refreshFiles(false));
    }
  }, [refreshFileList]);

  const handleFilePreview = async (file) => {
    if (!SUPPORTED_PREVIEW_FILE_TYPES.includes(file.type)) {
      setSnackbarMessage("File type not supported for previews");
      return;
    }
    setPreviewFile(file);
    try {
      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_url_for_download",
        {
          s3_key: file.s3_key,
        },
      );
      setPreviewFileLink(data.presigned_url);
    } catch (error) {
      setPreviewFileLink("");
      setPreviewFile(null);
      setSnackbarMessage("An error occurred when previewing file");
      console.error("Error previewing file:", error);
    }
  };

  const handleFileDownload = async (file) => {
    setDownloadingFileId(file.id);
    try {
      const { data } = await decoratedAxiosPost(
        "/get_s3_presigned_url_for_download",
        {
          s3_key: file.s3_key,
        },
      );

      const result = await axios(data.presigned_url, {
        method: "GET",
        responseType: "blob",
      });
      const fileURL = window.URL.createObjectURL(new Blob([result.data]));
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", file.name);
      document.body.appendChild(fileLink);
      fileLink.click();
      fileLink.parentNode.removeChild(fileLink);

      setDownloadingFileId("");
    } catch (error) {
      setDownloadingFileId("");
      setSnackbarMessage("An error occurred when downloading file");
      console.error("Error downloading file:", error);
    }
  };

  const initializeRowIdToItem = (rows) => {
    const newRowIdToItem = {};
    rows.forEach((row) => {
      newRowIdToItem[row.id] = row;
    });
    setRowIdToItem(newRowIdToItem);
  };

  const listProjectFiles = () => {
    setLoadingFiles(true);
    decoratedFetch(`/list_project_files/${project_id}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        const { files, folderPaths: paths } = parseListProjectFilesResponse(data);
        setProjectFiles(enrichFilesWithFolderPath(files, project_id));
        setFolderPaths(paths);
        setLoadingFiles(false);
      })
      .catch((error) => {
        setLoadingFiles(false);
        console.error(`Error listing files: ${error}`);
      });
  };

  useEffect(() => {
    if (value !== index) {
      return;
    }
    listProjectFiles();
  }, [value]);

  useEffect(() => {
    initializeRowIdToItem(visibleRows);
  }, [visibleRows]);

  const onUploadSuccess = () => {
    listProjectFiles();
  };

  const navigateToFolder = (folderPath) => {
    if (!folderPath) {
      setCurrentFolderPath("");
      setFolderBreadcrumbs([]);
      setSelectedFiles([]);
      return;
    }

    const crumbs = [];
    const parts = folderPath.split("/");
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join("/");
      crumbs.push({ path, name: part });
    });
    setCurrentFolderPath(folderPath);
    setFolderBreadcrumbs(crumbs);
    setSelectedFiles([]);
  };

  const navigateUp = () => {
    if (folderBreadcrumbs.length <= 1) {
      navigateToFolder("");
      return;
    }
    navigateToFolder(folderBreadcrumbs[folderBreadcrumbs.length - 2].path);
  };

  const handleFolderOpen = (params) => {
    if (params.row.is_folder && params.row.folder_full_path) {
      navigateToFolder(params.row.folder_full_path);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 4,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {params.row.is_folder ? <FolderIcon fontSize="small" /> : null}
            <Box
              component="span"
              sx={
                params.row.is_folder
                  ? {
                      cursor: "pointer",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }
                  : undefined
              }
            >
              {params.value}
            </Box>
          </Box>
        ),
      },
      { field: "type", headerName: "Type", flex: 1 },
      { field: "created_date", headerName: "Created Date", flex: 2 },
      {
        field: "actions",
        headerName: "",
        renderCell: (e) => {
          const row = e.row;
          if (row.is_folder) {
            return null;
          }
          let download = (
            <Grid item>
              <IconButton size="small" onClick={() => handleFileDownload(row)}>
                <DownloadIcon />
              </IconButton>
            </Grid>
          );
          if (row.id === downloadingFileId) {
            download = (
              <CircularProgress size="22px" sx={{ ml: "7px", mt: "14px" }} />
            );
          }
          let preview = (
            <Grid item sx={{ marginRight: "4px" }}>
              <IconButton size="small" onClick={() => handleFilePreview(row)}>
                <VisibilityIcon />
              </IconButton>
            </Grid>
          );
          if (row.id === previewFile?.id && !previewFileLink) {
            preview = (
              <CircularProgress
                size="22px"
                sx={{ mr: "12px", ml: "4px", mt: "14px" }}
              />
            );
          }
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <Grid container>
                {preview}
                {download}
              </Grid>
            </Box>
          );
        },
        width: 94,
        sortable: false,
        filterable: false,
      },
    ],
    [downloadingFileId, previewFile, previewFileLink],
  );

  const handleCreateFolder = async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName || trimmedName.includes("/")) {
      setSnackbarMessage("Folder name is required and cannot contain slashes");
      return;
    }

    setCreateFolderLoading(true);
    try {
      await decoratedAxiosPost("/create_project_folder", {
        project_id,
        name: trimmedName,
        folder_path: currentFolderPath,
      });
      setCreateFolderOpen(false);
      setNewFolderName("");
      setSnackbarMessage("Folder created");
      listProjectFiles();
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.msg || "An error occurred when creating folder",
      );
      console.error("Error creating folder:", error);
    } finally {
      setCreateFolderLoading(false);
    }
  };

  const handleMoveFiles = async () => {
    const fileIds = selectedFiles.filter((id) => !isFolderRowId(id));
    if (!fileIds.length) {
      setSnackbarMessage("Select files to move");
      return;
    }

    setMoveFilesLoading(true);
    try {
      await decoratedAxiosPost("/move_project_files", {
        project_id,
        file_ids: fileIds,
        folder_path: moveTargetFolderPath,
      });
      setMoveDialogOpen(false);
      setMoveTargetFolderPath("");
      setSelectedFiles([]);
      setSnackbarMessage(
        `Moved item${fileIds.length > 1 ? "s" : ""} successfully`,
      );
      listProjectFiles();
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.msg || "An error occurred when moving files",
      );
      console.error("Error moving files:", error);
    } finally {
      setMoveFilesLoading(false);
    }
  };

  const handleRemoveFiles = () => {
    setRemoveFilesLoading(true);
    decoratedFetch(`/delete_project_files/${project_id}`, {
      method: "POST",
      body: JSON.stringify({
        files: selectedFiles.map((rowId) => ({
          id: rowId,
          s3_key: rowIdToItem[rowId]?.s3_key,
        })),
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then(() => {
        setSnackbarMessage(
          `Deleted item${selectedFiles.length > 1 ? "s" : ""}`,
        );
        setSelectedFiles([]);
        setRemoveFilesLoading(false);
        listProjectFiles();
      })
      .catch((error) => {
        console.error(`Error deleting files: ${error}`);
        setSnackbarMessage(
          `An error occurred when deleting item${selectedFiles.length > 1 ? "s" : ""}`,
        );
        setRemoveFilesLoading(false);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handlePreviewClose = () => {
    setPreviewFile(null);
    setPreviewFileLink("");
  };

  const previewPrevFile = () => {
    const currentIdx = previewableFiles.findIndex(
      (file) => file.id === previewFile.id,
    );
    let prevIdx = currentIdx - 1;
    if (prevIdx < 0) {
      prevIdx = previewableFiles.length - 1;
    }
    handleFilePreview(previewableFiles[prevIdx]);
  };

  const previewNextFile = () => {
    const currentIdx = previewableFiles.findIndex(
      (file) => file.id === previewFile.id,
    );
    let nextIdx = currentIdx + 1;
    if (nextIdx >= previewableFiles.length) {
      nextIdx = 0;
    }
    handleFilePreview(previewableFiles[nextIdx]);
  };

  const openMoveDialog = () => {
    setMoveTargetFolderPath(currentFolderPath);
    setMoveDialogOpen(true);
  };

  return (
    <>
      {!previewFileLink ? null : (
        <FilePreview
          handleClose={handlePreviewClose}
          url={previewFileLink}
          file={previewFile}
          previewPrevFile={
            previewableFiles.length > 1 ? previewPrevFile : null
          }
          previewNextFile={
            previewableFiles.length > 1 ? previewNextFile : null
          }
        />
      )}
      <Snackbar
        sx={{
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        open={snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
      <Dialog
        open={createFolderOpen}
        onClose={() => !createFolderLoading && setCreateFolderOpen(false)}
      >
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateFolderOpen(false)}
            disabled={createFolderLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            disabled={createFolderLoading}
          >
            {createFolderLoading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={moveDialogOpen}
        onClose={() => !moveFilesLoading && setMoveDialogOpen(false)}
      >
        <DialogTitle>Move to Folder</DialogTitle>
        <DialogContent sx={{ minWidth: 320, pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="move-folder-label">Destination</InputLabel>
            <Select
              labelId="move-folder-label"
              label="Destination"
              value={moveTargetFolderPath}
              onChange={(e) => setMoveTargetFolderPath(e.target.value)}
            >
              <MenuItem value="">Project root</MenuItem>
              {selectableMoveTargets.map((folder) => (
                <MenuItem key={folder.path} value={folder.path}>
                  {folder.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMoveDialogOpen(false)}
            disabled={moveFilesLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleMoveFiles} disabled={moveFilesLoading}>
            {moveFilesLoading ? "Moving..." : "Move"}
          </Button>
        </DialogActions>
      </Dialog>
      <div style={{ width: "100%" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: "10px" }}
        >
          <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {currentFolderPath ? (
              <ThemedIconButton aria-label="go-back" onClick={navigateUp}>
                <ArrowBackIcon />
              </ThemedIconButton>
            ) : null}
            <Breadcrumbs
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  color: darkThemeEnabled
                    ? DARK_THEME_TEXT_COLOR
                    : LIGHT_THEME_TEXT_COLOR,
                },
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => navigateToFolder("")}
                sx={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <ThemedTypography component="span" variant="body2">
                  Files
                </ThemedTypography>
              </Box>
              {folderBreadcrumbs.map((folder) => (
                <Box
                  key={folder.path}
                  component="button"
                  type="button"
                  onClick={() => navigateToFolder(folder.path)}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <ThemedTypography component="span" variant="body2">
                    {folder.name}
                  </ThemedTypography>
                </Box>
              ))}
            </Breadcrumbs>
          </Grid>
          <Grid item>
            <Grid container alignItems="center" justifyContent="right">
              <Grid
                item
                sx={{
                  marginRight: "4px",
                  marginTop: "4px",
                  visibility: selectedFiles.length ? "visible" : "hidden",
                }}
              >
                {removeFilesLoading ? (
                  <CircularProgress
                    size="25px"
                    sx={{ marginTop: "7px", marginRight: "8px" }}
                  />
                ) : (
                  <ThemedIconButton
                    aria-label="remove-file"
                    onClick={handleRemoveFiles}
                  >
                    <RemoveIcon />
                  </ThemedIconButton>
                )}
              </Grid>
              <Grid
                item
                sx={{
                  marginRight: "4px",
                  marginTop: "4px",
                  visibility: selectedFiles.length ? "visible" : "hidden",
                }}
              >
                <ThemedIconButton
                  aria-label="move-files"
                  onClick={openMoveDialog}
                >
                  <DriveFileMoveIcon />
                </ThemedIconButton>
              </Grid>
              <Grid item sx={{ marginRight: "4px", marginTop: "4px" }}>
                <ThemedIconButton
                  aria-label="create-folder"
                  onClick={() => setCreateFolderOpen(true)}
                >
                  <CreateNewFolderIcon />
                </ThemedIconButton>
              </Grid>
              <Grid item>
                <FileUpload
                  onSuccess={onUploadSuccess}
                  folderPath={currentFolderPath}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Paper elevation={0} sx={{ height: "500px", width: "100%" }}>
          <ThemedDataGrid
            key={gridKey}
            rows={visibleRows.map((row) => ({
              id: row.id,
              name: row.name,
              type: row.is_folder ? "Folder" : row.type,
              created_date: formatDate(row.created_at),
              s3_key: row.s3_key,
              is_folder: row.is_folder,
              folder_full_path: row.folder_full_path,
            }))}
            columns={columns}
            pageSize={5}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectedFiles(newSelectionModel);
            }}
            onRowClick={handleFolderOpen}
            onRowDoubleClick={handleFolderOpen}
            onCellClick={(params, e) => {
              if (params.field === "actions") {
                e.stopPropagation();
              }
            }}
            getRowId={(row) => row.id}
            selectionModel={selectedFiles}
            checkboxSelection
            loading={loadingFiles}
            localeText={{ noRowsLabel: "No files" }}
          />
        </Paper>
      </div>
    </>
  );
}

export default FileTab;
